import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Input, Result, Spin, notification } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { ThemeForm, ThemeButton } from '../Styles/Theme';
import { LoginWrapper } from '../Login/LoginStyle';
import { validateSession, setPassword } from '../../services/invitation.service';
import { STORAGE_KEYS } from '../../utils/constants';

const SetPassword = () => {
  const navigate = useNavigate();
  const [form] = ThemeForm.useForm();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [errorCode, setErrorCode] = useState(null);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const sessionToken = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
    const pendingUser = localStorage.getItem(STORAGE_KEYS.PENDING_USER);

    if (!sessionToken) {
      setStatus('error');
      setMessage('Invalid session. Please use the invitation link from your email.');
      setErrorCode('INVALID_SESSION');
      return;
    }

    // Try to get user name from stored data
    if (pendingUser) {
      try {
        const user = JSON.parse(pendingUser);
        setUserName(user.name || '');
      } catch (e) {
        // Ignore parse errors
      }
    }

    const validate = async () => {
      const result = await validateSession(sessionToken);

      if (result.success) {
        setStatus('ready');
        setUserName(result.data?.user?.name || userName);
      } else {
        setStatus('error');
        setMessage(result.message);
        setErrorCode(result.data?.code || 'SESSION_EXPIRED');

        // Clear stored session data
        localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.PENDING_USER);
      }
    };

    validate();
  }, []);

  const handleSubmit = async (values) => {
    const sessionToken = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);

    if (!sessionToken) {
      notification.error({
        message: 'Session Expired',
        description: 'Please use the invitation link from your email again.',
      });
      return;
    }

    setLoading(true);

    const result = await setPassword(sessionToken, values.password, values.confirmPassword);

    setLoading(false);

    if (result.success) {
      // Clear stored session data
      localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.PENDING_USER);

      notification.success({
        message: 'Success',
        description: 'Your password has been set successfully. You can now login.',
      });

      // Redirect to login (replace history so back button won't return here)
      navigate('/login', { replace: true });
    } else {
      notification.error({
        message: 'Error',
        description: result.message,
      });
    }
  };

  // Loading state - same design as login page
  if (status === 'loading') {
    return (
      <LoginWrapper>
        <Row gutter={[60, 16]}>
          <Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 10, order: 1 }} xl={{ span: 10, order: 1 }}>
            <div className='loginLogo'>
              <img src='/images/logo.svg' alt="CrowdVox" />
            </div>
            <div className='loginContainer'>
              <div style={{ textAlign: 'center', paddingTop: '60px' }}>
                <Spin size="large" />
                <h1 style={{ marginTop: '24px', fontSize: '24px' }}>Validating Session...</h1>
                <p style={{ color: '#6F7B8C', fontSize: '14px' }}>Please wait while we validate your session.</p>
              </div>
            </div>
          </Col>
          <Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 14, order: 1 }} xl={{ span: 14, order: 1 }}>
            <div className='loginBanner'>
              <img className='w-full h-full' src='/images/change-password-banner.png' alt='CrowdVox' />
            </div>
          </Col>
        </Row>
      </LoginWrapper>
    );
  }

  // Error state - antd Result page (like 404)
  if (status === 'error') {
    const getErrorTitle = () => {
      switch (errorCode) {
        case 'ALREADY_ACTIVE':
          return 'Account Already Active';
        case 'SESSION_EXPIRED':
        case 'INVALID_SESSION':
          return 'Link Expired or Invalid';
        default:
          return 'Session Invalid';
      }
    };

    const getErrorMessage = () => {
      switch (errorCode) {
        case 'ALREADY_ACTIVE':
          return 'Your account is already set up. You can login with your credentials.';
        case 'SESSION_EXPIRED':
        case 'INVALID_SESSION':
          return 'This invitation link has expired or has already been used. Please contact your administrator to receive a new invitation.';
        default:
          return message || 'Something went wrong. Please contact your administrator for assistance.';
      }
    };

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '20px',
        backgroundColor: '#f5f5f5'
      }}>
        <Result
          status={errorCode === 'ALREADY_ACTIVE' ? 'info' : 'error'}
          title={getErrorTitle()}
          subTitle={getErrorMessage()}
          extra={
            <ThemeButton size="large" className="loginBtn" onClick={() => navigate('/login', { replace: true })}>
              Go to Login
            </ThemeButton>
          }
        />
      </div>
    );
  }

  // Ready state - Login page style form
  return (
    <LoginWrapper>
      <Row gutter={[60, 16]}>
        <Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 10, order: 1 }} xl={{ span: 10, order: 1 }}>
          <div className='loginLogo'>
            <img src='/images/logo.svg' alt="CrowdVox" />
          </div>
          <div className='loginContainer'>
            <h1>Set Your Password</h1>
            {userName && (
              <p>Welcome, <strong>{userName}</strong>! Please set your password to continue.</p>
            )}
            <ThemeForm
              form={form}
              name="setPasswordForm"
              layout="vertical"
              autoComplete="off"
              onFinish={handleSubmit}
              variant='underlined'
            >
              <ThemeForm.Item
                name="password"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter your password' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                  { max: 20, message: 'Password must not exceed 20 characters' },
                  {
                    pattern: /[A-Z]/,
                    message: 'Password must contain at least one uppercase letter',
                  },
                  {
                    pattern: /[a-z]/,
                    message: 'Password must contain at least one lowercase letter',
                  },
                  {
                    pattern: /\d/,
                    message: 'Password must contain at least one number',
                  },
                  {
                    pattern: /[@$!%*?&]/,
                    message: 'Password must contain at least one special character (@$!%*?&)',
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className='mr-3' />}
                  size={'large'}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
              </ThemeForm.Item>
              <ThemeForm.Item
                className='mb-2 mt-2'
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className='mr-3' />}
                  size={'large'}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
              </ThemeForm.Item>
              <ThemeForm.Item className="mt-8">
                {loading ? (
                  <ThemeButton size='large' loading className='loginBtn'>
                    Setting Password...
                  </ThemeButton>
                ) : (
                  <ThemeButton size='large' className='loginBtn' htmlType="submit">
                    Set Password
                  </ThemeButton>
                )}
              </ThemeForm.Item>
            </ThemeForm>
          </div>
        </Col>
        <Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 14, order: 1 }} xl={{ span: 14, order: 1 }}>
          <div className='loginBanner'>
            <img className='w-full h-full' src='/images/change-password-banner.png' alt='CrowdVox' />
          </div>
        </Col>
      </Row>
    </LoginWrapper>
  );
};

export default SetPassword;
